import React, { useState, useEffect } from 'react';
import {
    Paper,
    Box,
    Typography,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import moment from 'moment';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    Label
} from 'recharts';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { zhTW } from 'date-fns/locale';
import { getDailyDetectionByType } from '../services/cctvService';

// 各 object_type 對應顏色
const OBJECT_TYPE_COLORS = {
    car: '#3D5AFE',        // 藍
    motorcycle: '#FF9100', // 橘
    bus: '#00B0FF',        // 淺藍
    truck: '#F44336',      // 紅
    bicycle: '#76FF03',    // 綠
    person: '#D500F9',     // 紫
    van: '#FFEA00',        // 黃
    other: '#90A4AE'       // 灰
};

// object_type 中文對應
const OBJECT_TYPE_LABELS = {
    car: '小客車',
    motorcycle: '機車',
    bus: '公車',
    truck: '卡車',
    bicycle: '自行車',
    person: '行人',
    van: '廂型車',
    other: '其他'
};

const CctvDetectionTypeChart = ({ cctvData = [], teamName, minDate }) => {
    const [date, setDate] = useState(moment().format('YYYY-MM-DD'));
    const [selectedCamera, setSelectedCamera] = useState('');
    const [chartData, setChartData] = useState([]);
    const [objectTypes, setObjectTypes] = useState([]);
    const [visibleTypes, setVisibleTypes] = useState({});

    // 當 cctvData 載入後，自動選第一台攝影機
    useEffect(() => {
        if (cctvData.length > 0 && !selectedCamera) {
            setSelectedCamera(cctvData[0].code);
        }
    }, [cctvData]);

    // 每次日期或攝影機變更時重新抓資料
    useEffect(() => {
        if (!selectedCamera) return;

        const fetchData = async () => {
            try {
                const res = await getDailyDetectionByType(date, selectedCamera, teamName);
                const types = res.objectTypes || [];
                setObjectTypes(types);

                // 初始化可見性
                const visibility = {};
                types.forEach((t) => { visibility[t] = true; });
                setVisibleTypes(visibility);

                setChartData(res.stats || []);
            } catch (error) {
                console.error('CctvDetectionTypeChart fetchData 錯誤:', error);
                setChartData([]);
                setObjectTypes([]);
            }
        };

        fetchData();
    }, [date, selectedCamera, teamName]);

    const handleDateChange = (newDate) => {
        setDate(moment(newDate).format('YYYY-MM-DD'));
    };

    const toggleType = (type) => {
        setVisibleTypes((prev) => {
            const isSingle = Object.values(prev).filter((v) => v).length === 1 && prev[type];
            if (isSingle) {
                const all = {};
                objectTypes.forEach((t) => { all[t] = true; });
                return all;
            }
            const single = {};
            objectTypes.forEach((t) => { single[t] = t === type; });
            return single;
        });
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (!active || !payload || payload.length === 0) return null;
        return (
            <div style={{ background: '#fff', padding: '10px', border: '1px solid #ccc' }}>
                <p style={{ margin: 0, fontWeight: 'bold' }}>{`時間: ${label}`}</p>
                {payload.map((entry, i) => (
                    <p key={i} style={{ margin: 0, color: entry.color }}>
                        {`${OBJECT_TYPE_LABELS[entry.dataKey] || entry.dataKey}: ${entry.value}`}
                    </p>
                ))}
                <p style={{ margin: 0, fontWeight: 'bold' }}>
                    {`總數: ${payload.reduce((sum, e) => sum + e.value, 0)}`}
                </p>
            </div>
        );
    };

    const CustomLegend = ({ payload }) => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', mt: 1 }}>
            {payload.map((entry, i) => (
                <Box
                    key={i}
                    sx={{ display: 'inline-flex', alignItems: 'center', m: 0.5, cursor: 'pointer' }}
                    onClick={() => toggleType(entry.dataKey)}
                >
                    <Box sx={{ width: 10, height: 10, backgroundColor: entry.color, mr: 0.5 }} />
                    <Typography
                        variant="body2"
                        sx={{
                            position: 'relative',
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                left: 0, right: 0, top: '50%',
                                borderTop: visibleTypes[entry.dataKey] ? 'none' : '1px solid black'
                            }
                        }}
                    >
                        {OBJECT_TYPE_LABELS[entry.dataKey] || entry.dataKey}
                    </Typography>
                </Box>
            ))}
        </Box>
    );

    return (
        <Paper sx={{ p: 2, height: '100%' }}>
            {/* 工具列 */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                {/* 攝影機選擇器 */}
                <FormControl variant="standard" sx={{ minWidth: 180 }}>
                    <InputLabel>選擇攝影機</InputLabel>
                    <Select
                        value={selectedCamera}
                        onChange={(e) => setSelectedCamera(e.target.value)}
                    >
                        {cctvData.map((cctv) => (
                            <MenuItem key={cctv.code} value={cctv.code}>
                                {cctv.name || cctv.code}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* 日期選擇器 */}
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={zhTW}>
                    <DatePicker
                        label="選擇日期"
                        value={date ? new Date(`${date}T00:00:00`) : null}
                        onChange={handleDateChange}
                        minDate={minDate || null}
                        maxDate={new Date()}
                        renderInput={(params) => <TextField {...params} variant="standard" />}
                    />
                </LocalizationProvider>
            </Box>

            {/* 無資料提示 */}
            {chartData.length === 0 || objectTypes.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                    <Typography variant="body1" color="text.secondary">
                        {selectedCamera ? '目前無辨識事件資料' : '請先選擇攝影機'}
                    </Typography>
                </Box>
            ) : (
                <ResponsiveContainer width="100%" height={430}>
                    <BarChart
                        barSize={20}
                        data={chartData}
                        margin={{ top: 20, right: 30, left: 40, bottom: 30 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="timeTag" angle={-45} textAnchor="end" interval={0} dy={10}>
                            <Label value="時間(小時)" position="insideBottom" offset={-60} />
                        </XAxis>
                        <YAxis>
                            <Label value="偵測次數" angle={-90} position="insideLeft" dx={-10} />
                        </YAxis>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend content={<CustomLegend />} />
                        {objectTypes.map((type) => (
                            <Bar
                                key={type}
                                dataKey={type}
                                stackId="a"
                                fill={OBJECT_TYPE_COLORS[type] || '#90A4AE'}
                                name={OBJECT_TYPE_LABELS[type] || type}
                                hide={!visibleTypes[type]}
                            />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            )}
        </Paper>
    );
};

export default CctvDetectionTypeChart;
